import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as EntityIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Refresh as RefreshIcon,
  Payment as ServiceIcon,
  PhoneAndroid as RechargeIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import api from '../../../services/api';

interface SispEntity {
  id: string;
  descricao: string;
  codigoSisp: string;
  tipo: string;
  estado: string;
  tokenSisp?: string;
  posId?: string;
  posAuthCode?: string;
}

interface Stats {
  total: number;
  servico: number;
  recarga: number;
  activas: number;
  inactivas: number;
}

const SispEntities: React.FC = () => {
  const { t } = useTranslation();
  const [entities, setEntities] = useState<SispEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [stats, setStats] = useState<Stats>({
    total: 0,
    servico: 0,
    recarga: 0,
    activas: 0,
    inactivas: 0,
  });

  // Edit dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<SispEntity | null>(null);
  const [formData, setFormData] = useState({
    codigoSisp: '',
    tokenSisp: '',
    estado: 'A',
    tipo: 'Serviço',
  });
  const [saving, setSaving] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Register dialog state
  const [registerDialog, setRegisterDialog] = useState(false);
  const [unregisteredEntities, setUnregisteredEntities] = useState<{ id: string; descricao: string }[]>([]);
  const [loadingUnregistered, setLoadingUnregistered] = useState(false);
  const [registerFormData, setRegisterFormData] = useState({
    entityId: '',
    codigoSisp: '',
    tokenSisp: '',
    estado: 'A',
    tipo: 'Serviço',
  });
  const [registering, setRegistering] = useState(false);

  const showToast = (message: string, severity: 'success' | 'error') => {
    setToast({ open: true, message, severity });
  };

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    try {
      // Pelo cliente `api`: leva o token. O router exige PFADM desde 19/10.
      const { data } = await api.get('/admin/parametrizacoes/sisp-entities');
      if (data.success) {
        setEntities(data.data || []);
        if (data.stats) {
          setStats(data.stats);
        }
      }
    } catch (error) {
      console.error('Error fetching SISP entities:', error);
      showToast(t('configSispEntities.errorLoading'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  const getTypeChip = (tipo: string) => {
    return tipo === 'Recarga' ? (
      <Chip size="small" color="secondary" label={t('configSispEntities.recharge')} icon={<RechargeIcon />} />
    ) : (
      <Chip size="small" color="primary" label={t('common.service')} icon={<ServiceIcon />} />
    );
  };

  const getStatusChip = (estado: string) => {
    return estado === 'A' ? (
      <Chip size="small" color="success" label={t('configSispEntities.activeFemale')} icon={<ActiveIcon />} />
    ) : (
      <Chip size="small" color="default" label={t('configSispEntities.inactiveFemale')} icon={<InactiveIcon />} />
    );
  };

  const filteredEntities = entities.filter(entity => {
    const matchesSearch = searchTerm === '' ||
      entity.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entity.codigoSisp?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' ||
      (typeFilter === 'servico' && entity.tipo === 'Serviço') ||
      (typeFilter === 'recarga' && entity.tipo === 'Recarga');
    return matchesSearch && matchesType;
  });

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (entity: SispEntity) => {
    setSelectedEntity(entity);
    setFormData({
      codigoSisp: entity.codigoSisp || '',
      tokenSisp: entity.tokenSisp || '',
      estado: entity.estado || 'A',
      tipo: entity.tipo || 'Serviço',
    });
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedEntity(null);
  };

  const handleSave = async () => {
    if (!selectedEntity) return;

    setSaving(true);
    try {
      const { data } = await api.put(`/admin/parametrizacoes/sisp-entities/${selectedEntity.id}`, formData);

      if (data.success) {
        showToast(t('configSispEntities.entityUpdated'), 'success');
        handleCloseEdit();
        fetchEntities();
      } else {
        showToast(data.error || t('configSispEntities.errorSaving'), 'error');
      }
    } catch (error) {
      console.error('Error saving SISP entity:', error);
      showToast(t('configSispEntities.errorSaving'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entity: SispEntity) => {
    if (!window.confirm(t('configSispEntities.confirmRemove', { name: entity.descricao }))) {
      return;
    }

    try {
      const { data } = await api.delete(`/admin/parametrizacoes/sisp-entities/${entity.id}`);

      if (data.success) {
        showToast(t('configSispEntities.codeRemoved'), 'success');
        fetchEntities();
      } else {
        showToast(data.error || t('configSispEntities.errorRemoving'), 'error');
      }
    } catch (error) {
      console.error('Error deleting SISP entity:', error);
      showToast(t('configSispEntities.errorRemoving'), 'error');
    }
  };

  const handleOpenRegister = async () => {
    setRegisterDialog(true);
    setRegisterFormData({ entityId: '', codigoSisp: '', tokenSisp: '', estado: 'A', tipo: 'Serviço' });
    setLoadingUnregistered(true);
    try {
      const { data } = await api.get('/admin/parametrizacoes/sisp-entities/unregistered');
      if (data.success) {
        setUnregisteredEntities(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching unregistered entities:', error);
      showToast(t('configSispEntities.errorLoadingAvailable'), 'error');
    } finally {
      setLoadingUnregistered(false);
    }
  };

  const handleRegister = async () => {
    if (!registerFormData.entityId || !registerFormData.codigoSisp) return;

    setRegistering(true);
    try {
      const { data } = await api.put(`/admin/parametrizacoes/sisp-entities/${registerFormData.entityId}`, {
        codigoSisp: registerFormData.codigoSisp,
        tokenSisp: registerFormData.tokenSisp,
        estado: registerFormData.estado,
        tipo: registerFormData.tipo,
      });
      if (data.success) {
        showToast(t('configSispEntities.entityRegistered'), 'success');
        setRegisterDialog(false);
        fetchEntities();
      } else {
        showToast(data.error || t('configSispEntities.errorRegistering'), 'error');
      }
    } catch (error) {
      console.error('Error registering SISP entity:', error);
      showToast(t('configSispEntities.errorRegistering'), 'error');
    } finally {
      setRegistering(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {t('configSispEntities.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('configSispEntities.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenRegister}>
            {t('configSispEntities.register')}
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchEntities}>
            {t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EntityIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('common.total')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ServiceIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.servico}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('configSispEntities.services')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RechargeIcon color="secondary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.recarga}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('configSispEntities.recharges')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.activas}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('configSispEntities.active')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InactiveIcon color="disabled" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.inactivas}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('configSispEntities.inactive')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('configSispEntities.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.type')}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="servico">{t('common.service')}</MenuItem>
                <MenuItem value="recarga">{t('configSispEntities.recharge')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Entities Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.description')}</TableCell>
                    <TableCell width={120} align="center">{t('configSispEntities.sispCode')}</TableCell>
                    <TableCell width={130}>{t('common.type')}</TableCell>
                    <TableCell width={120}>{t('common.status')}</TableCell>
                    <TableCell width={100} align="center">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEntities.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          {t('configSispEntities.noEntities')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredEntities
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((entity) => (
                        <TableRow key={entity.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {entity.descricao}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={entity.codigoSisp}
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>{getTypeChip(entity.tipo)}</TableCell>
                          <TableCell>{getStatusChip(entity.estado)}</TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => handleOpenEdit(entity)} title={t('common.edit')}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(entity)}
                              title={t('configSispEntities.removeSispCode')}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredEntities.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage={t('common.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => t('common.paginationDisplay', { from, to, count })}
            />
          </>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EntityIcon />
          {t('configSispEntities.editEntity')}
        </DialogTitle>
        <DialogContent dividers>
          {selectedEntity && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">{t('common.entity')}</Typography>
              <Typography variant="body1" fontWeight={600}>{selectedEntity.descricao}</Typography>
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={t('configSispEntities.sispCode')}
                value={formData.codigoSisp}
                onChange={(e) => setFormData({ ...formData, codigoSisp: e.target.value })}
                helperText={t('configSispEntities.sispCodeHelper')}
                slotProps={{
                  htmlInput: { style: { fontFamily: 'monospace' } },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('common.type')}
                value={formData.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                helperText={t('configSispEntities.transactionType')}
              >
                <MenuItem value="Compra">{t('configSispEntities.purchase')}</MenuItem>
                <MenuItem value="Serviço">{t('configSispEntities.servicePayment')}</MenuItem>
                <MenuItem value="Recarga">{t('configSispEntities.rechargePrePaid')}</MenuItem>
                <MenuItem value="Estorno">{t('configSispEntities.refund')}</MenuItem>
                <MenuItem value="Token Requisicao">{t('configSispEntities.tokenRequest')}</MenuItem>
                <MenuItem value="Token Pagamento">{t('configSispEntities.tokenPayment')}</MenuItem>
                <MenuItem value="Token Cancelar">{t('configSispEntities.tokenCancel')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
              >
                <MenuItem value="A">{t('configSispEntities.activeFemale')}</MenuItem>
                <MenuItem value="I">{t('configSispEntities.inactiveFemale')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('configSispEntities.sispToken')}
                value={formData.tokenSisp}
                onChange={(e) => setFormData({ ...formData, tokenSisp: e.target.value })}
                helperText={t('configSispEntities.sispTokenHelper')}
                slotProps={{
                  htmlInput: { style: { fontFamily: 'monospace' } },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.codigoSisp || saving}
            startIcon={saving ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Register Dialog */}
      <Dialog open={registerDialog} onClose={() => setRegisterDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon />
          {t('configSispEntities.registerEntity')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              {loadingUnregistered ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <TextField
                  fullWidth
                  select
                  label={t('common.entity')}
                  value={registerFormData.entityId}
                  onChange={(e) => setRegisterFormData({ ...registerFormData, entityId: e.target.value })}
                  helperText={t('configSispEntities.selectEntity')}
                >
                  {unregisteredEntities.map((e) => (
                    <MenuItem key={e.id} value={e.id}>{e.descricao}</MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={t('configSispEntities.sispCode')}
                value={registerFormData.codigoSisp}
                onChange={(e) => setRegisterFormData({ ...registerFormData, codigoSisp: e.target.value })}
                helperText={t('configSispEntities.sispCodeHelper')}
                slotProps={{
                  htmlInput: { style: { fontFamily: 'monospace' } },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('common.type')}
                value={registerFormData.tipo}
                onChange={(e) => setRegisterFormData({ ...registerFormData, tipo: e.target.value })}
                helperText={t('configSispEntities.transactionType')}
              >
                <MenuItem value="Compra">{t('configSispEntities.purchase')}</MenuItem>
                <MenuItem value="Serviço">{t('configSispEntities.servicePayment')}</MenuItem>
                <MenuItem value="Recarga">{t('configSispEntities.rechargePrePaid')}</MenuItem>
                <MenuItem value="Estorno">{t('configSispEntities.refund')}</MenuItem>
                <MenuItem value="Token Requisicao">{t('configSispEntities.tokenRequest')}</MenuItem>
                <MenuItem value="Token Pagamento">{t('configSispEntities.tokenPayment')}</MenuItem>
                <MenuItem value="Token Cancelar">{t('configSispEntities.tokenCancel')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={registerFormData.estado}
                onChange={(e) => setRegisterFormData({ ...registerFormData, estado: e.target.value })}
              >
                <MenuItem value="A">{t('configSispEntities.activeFemale')}</MenuItem>
                <MenuItem value="I">{t('configSispEntities.inactiveFemale')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('configSispEntities.sispToken')}
                value={registerFormData.tokenSisp}
                onChange={(e) => setRegisterFormData({ ...registerFormData, tokenSisp: e.target.value })}
                helperText={t('configSispEntities.sispTokenHelper')}
                slotProps={{
                  htmlInput: { style: { fontFamily: 'monospace' } },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisterDialog(false)} disabled={registering}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleRegister}
            disabled={!registerFormData.entityId || !registerFormData.codigoSisp || registering}
            startIcon={registering ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {registering ? t('configSispEntities.registering') : t('configSispEntities.register')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SispEntities;
